import quickFetch from 'quick-fetch';
import { getChirpEarlyRequestName } from 'rpc-client-utils';
export const getEarlyRequestResult = (name, fallbackFetcher) => {
  const earlyRequest = name && quickFetch.getRequestStateByName(name);
  if (!earlyRequest) {
    if (!fallbackFetcher) {
      throw new Error('No fallback fetcher provided');
    }
    return fallbackFetcher();
  }
  return new Promise((resolve, reject) => {
    earlyRequest.whenFinished(result => {
      quickFetch.removeEarlyRequest(name);
      resolve(result);
    });
    earlyRequest.onError((__xhr, errorMessage) => {
      quickFetch.removeEarlyRequest(name);
      reject(new Error(errorMessage));
    });
  });
};

/**
 * Gets a CHIRP early request result, or falls back to making the RPC call.
 *
 * IMPORTANT: The details and request object must EXACTLY match what was passed to
 * makeChirpEarlyRequest() in the early requester, or the hash won't match.
 *
 * @param details - The RPC details object with { ...rpc, earlyRequest: true }
 * @param requestObject - The request parameters { request: {...} }
 * @param fallbackFetcher - Function to call if early request not found
 */
export const getChirpEarlyRequest = ({
  details,
  requestObject,
  fallbackFetcher
}) => {
  // Compute the early request name using the same algorithm as makeChirpEarlyRequest
  const requestName = getChirpEarlyRequestName(details, requestObject);
  const requestState = quickFetch.getRequestStateByName(requestName);
  if (requestState && !requestState.error) {
    return new Promise((resolve, reject) => {
      requestState.whenFinished(response => {
        quickFetch.removeEarlyRequest(requestName);
        try {
          const parsed = JSON.parse(response);
          resolve(parsed.data || parsed);
        } catch (e) {
          // Truncated response (e.g. network interruption mid-stream).
          // Fall back to a fresh RPC call instead of failing the page.
          fallbackFetcher().then(resolve).catch(reject);
        }
      });
      requestState.onError((xhr, errorMessage) => {
        quickFetch.removeEarlyRequest(requestName);
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (typeof parsed === 'object' && (parsed.type === 'rpcError' || parsed.type === 'internalError')) {
            resolve(parsed);
          } else {
            reject(new Error(errorMessage));
          }
        } catch (e) {
          reject(new Error(errorMessage));
        }
      });
    });
  }
  return fallbackFetcher();
};